import { styled } from "goober";
import type { ReactNode } from "react";
import { ToggleButton } from "./HeadingToggle";
import { InfoIcon } from "./InfoIcon";
import { PanelToggleIcon } from "./PanelToggleIcon";
import { ShortcutHint } from "./ShortcutHint";
import { Tooltip } from "./Tooltip";

export type PanelSide = "left" | "right";

export const sidePanelWidth = 228;

export type PanelShortcut = { code: string; hint: string; keys: string };

export const filesShortcut: PanelShortcut = {
  code: "KeyF",
  hint: "⇧⌥F",
  keys: "Shift+Alt+F",
};

export const executionsShortcut: PanelShortcut = {
  code: "KeyE",
  hint: "⇧⌥E",
  keys: "Shift+Alt+E",
};

const Slot = styled("div")`
  display: flex;
  flex-shrink: 0;
  width: ${sidePanelWidth}px;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  transition: width var(--motion-base) var(--ease-out);

  &[data-expanded="false"] {
    width: 0;
  }
`;

function SideIcon({ side }: { side: PanelSide }) {
  return (
    <span
      style={{
        display: "flex",
        transform: side === "right" ? "scaleX(-1)" : undefined,
      }}
    >
      <PanelToggleIcon />
    </span>
  );
}

// Hover previews the collapse by shrinking the rail, like the chatpad toggle.
const Collapse = styled(ToggleButton)`
  border-radius: 9999px;

  & svg {
    opacity: 0.2;
  }
  &:hover svg {
    opacity: 1;
  }

  & .inner {
    transition: width var(--motion-base) var(--ease-out);
  }
  &:hover .inner {
    width: 1px;
  }
`;

export function PanelCollapseButton({
  label,
  side,
  shortcut,
  onPress,
}: {
  label: string;
  side: PanelSide;
  shortcut: PanelShortcut;
  onPress: () => void;
}) {
  return (
    <Tooltip
      content={
        <>
          {label} <ShortcutHint>{shortcut.hint}</ShortcutHint>
        </>
      }
    >
      <Collapse
        aria-label={label}
        aria-keyshortcuts={shortcut.keys}
        onPress={onPress}
      >
        <SideIcon side={side} />
      </Collapse>
    </Tooltip>
  );
}

// Mounts only once measured, so a pinned panel appears in place instead of
// sliding open on every load.
export function PinnedPanelSlot({
  measured,
  open,
  children,
}: {
  measured: boolean;
  open: boolean;
  children: ReactNode;
}) {
  if (!measured) return null;
  return (
    <Slot data-expanded={open} aria-hidden={!open || undefined} inert={!open}>
      {children}
    </Slot>
  );
}

// Wide enough for the longest locale timestamp, so only written labels truncate.
export const OpenerLabel = styled("span")`
  display: flex;
  min-width: 0;
  max-width: 24ch;
`;

export function pinPanelSection({
  label,
  side,
  pinned,
  onToggle,
}: {
  label: string;
  side: PanelSide;
  pinned: boolean;
  onToggle: () => void;
}) {
  return {
    stickyBottom: true,
    items: [
      {
        type: "button" as const,
        key: "pin",
        label: pinned ? (
          // The dropdown only shows while pinned when the panel lacks room.
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            {label}
            <Tooltip content="Opens when there’s more room">
              <InfoIcon role="img" aria-label="Opens when there’s more room" />
            </Tooltip>
          </span>
        ) : (
          label
        ),
        left: <SideIcon side={side} />,
        toggled: pinned,
        onClick: onToggle,
      },
    ],
  };
}
